import React, { FC, useState, useEffect } from 'react';
import { Grid, Box, Button } from '@mui/material';
import RefreshIcon from '@mui/icons-material/Refresh';
import { useTranslation } from 'react-i18next';
import HelpButton from 'components/shared/help-button/help-button.component';
import ClinicFilter from 'components/shared/clinic-filter.component';
import InvoiceFilterSelect from 'components/invoices/invoice-filter-select/invoice-filter-select.component';
import InvoiceFilterFreeText from 'components/invoices/invoice-filter-free-text/invoice-filter-free-text.component';
import { InvoiceNoteFilter, InvoiceNoteStatus } from 'models/invoice';
import AddIcon from '@mui/icons-material/Add';
import { Organization } from 'models/organization';

type DebitCreditNotesActionsProps = {
    onInvoiceNoteFilterChange: (invoiceNoteFilter: InvoiceNoteFilter) => void;
    filters: InvoiceNoteFilter;
    openCreateInvoiceNoteDialog: () => void;
};

const DebitCreditNotesActions: FC<DebitCreditNotesActionsProps> = ({
    onInvoiceNoteFilterChange,
    filters,
    openCreateInvoiceNoteDialog,
}: DebitCreditNotesActionsProps) => {
    const { t: translate } = useTranslation(['common', 'invoices']);
    const [invoiceNoteFilter, setInvoiceNoteFilter] = useState<InvoiceNoteFilter>();
    const [invoiceStatus, setInvoiceStatus] = useState(InvoiceNoteStatus.NOT_INVOICED);

    useEffect(() => {
        setInvoiceNoteFilter(filters);
        setInvoiceStatus(filters.invoiceNoteStatus || InvoiceNoteStatus.NOT_INVOICED);
    }, [filters]);

    const freeTextHandler = (text: string) => {
        setInvoiceNoteFilter({ ...invoiceNoteFilter, search: text });
    };

    const clinicSelectHandler = (_: React.SyntheticEvent, clinic: Organization | null) => {
        setInvoiceNoteFilter({ ...invoiceNoteFilter, clinicOrganizationId: clinic?._id as string, clinic });
    };

    const invoiceStatusSelectHandler = (invoiceStatusKey: string) => {
        const statusFilter = Object.entries(InvoiceNoteStatus).find(([key]) => {
            return key === invoiceStatusKey;
        });
        const filter: InvoiceNoteFilter = {
            ...invoiceNoteFilter,
            invoiceNoteStatus: statusFilter ? statusFilter[1] : InvoiceNoteStatus.NOT_INVOICED,
        };
        setInvoiceStatus(invoiceStatusKey as InvoiceNoteStatus);
        setInvoiceNoteFilter(filter);
    };

    const filter = () => {
        onInvoiceNoteFilterChange(invoiceNoteFilter || {});
    };

    return (
        <Grid container spacing={1}>
            <Grid item md={2} sm={6} xs={12}>
                <InvoiceFilterFreeText
                    initialValue={invoiceNoteFilter?.search}
                    label={'SEARCH_DEBIT_CREDIT_NOTES'}
                    onFreeTextChange={freeTextHandler}
                />
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
                <ClinicFilter
                    showParents
                    onlyCollective
                    includeCollectiveDentist
                    selectedClinic={invoiceNoteFilter?.clinic}
                    onClinicFilterChange={clinicSelectHandler}
                />
            </Grid>
            <Grid item md={2} sm={6} xs={12}>
                <InvoiceFilterSelect
                    label="common:STATUS"
                    menuItems={[InvoiceNoteStatus.NOT_INVOICED, InvoiceNoteStatus.INVOICED]}
                    optionSelected={invoiceStatus}
                    onSelectFilterChange={invoiceStatusSelectHandler}
                />
            </Grid>
            <Grid item md={2} sm={6} xs={12}>
                <Button onClick={filter} size="medium" type="button" startIcon={<RefreshIcon />}>
                    {translate('common:REFRESH_LIST')}
                </Button>
            </Grid>
            <Grid item md={3} sm={6} xs={12}>
                <Box display="flex" justifyContent="flex-end">
                    <Box mr={2}>
                        <HelpButton
                            title={translate('invoices:DEBIT_CREDIT_NOTES')}
                            helpText={translate('invoices:CREDIT_DEBIT_NOTES_HELP_TEXT')}
                        />
                    </Box>
                    <Button onClick={() => openCreateInvoiceNoteDialog()} startIcon={<AddIcon />}>
                        {translate('invoices:CREATE_NOTE')}
                    </Button>
                </Box>
            </Grid>
        </Grid>
    );
};

export default DebitCreditNotesActions;
